/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, debounceTime, catchError } from 'rxjs/operators';

import * as actionType from '../actions';
import RemoteCase from '../../../case/remote-case';
import * as StateManager from '../state-manager';
import { logAndCatchError } from '../../../utils/rxjs-utils';
import services from '../../../services';
import loadComponents from '../../../utils/component-loader';
import { publishResponse } from '../../../observables/stream-observable';
import { fetch } from '../../../service_broker/fetch';
import Constants, { publicConstants } from '../../../constants';
import { getSemanticURL } from '../../../router/app-router';
import RouteParser from '../../../router/route-parser';
import Utils from '../../../utils/utils';
import NavigationUtils from '../../../utils/navigation-utils';
import { getTranslatedDXAPIResponse } from '../../../utils/dxapi-response-translator';
import {
  isContainerSingleMode,
  hasContainerItems,
  getActiveContainerItemName
} from '../../../container/container-utils';
import PubSubUtils from '../../../utils/pubsub-utils';
import HeaderProcessor from '../../../service_broker/header-processor';
import Debugger from '../../../debug/debugger';
import type { Action } from '../state-manager';
import type { ActionObject } from '../../../globals';
import ObjectTransformer from '../../../utils/object-transformer';
import EnvironmentInfo from '../../../environment-info';
import { resetSemanticURLFromAssignmentToCase } from '../../../case/common-helper';
import * as BrowserHistory from '../../../router/browser-router';
import { getSemanticURLWithRelatedQueryParams } from '../../../router/helper';
import * as actions from '../actions/action-creators';
import CaseUtils from '../../../case/utils';

import {
  getActionPayload,
  getUpdatedPayloadBasedOnResourceType,
  processHeaders,
  getActionBasedOnContainerMode,
  processDataToDispatchErrorAction,
  addPreviewResetAction,
  getResourceType,
  handleLaunchAssignmentError,
  setCoexistenceAction
} from './util';

const {
  RESOURCE_TYPES: { CASE, DATA, OBJECT }
} = publicConstants;

let theReturn: any[] = [];
let isGetObjectEndpoint = false;
let isShowPageCallStarted = false;
const requestBody: { dataContext?: any; dataContextParameters?: any } = {};
const showHistory = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(actionType.SHOW_HISTORY),
    debounceTime(200),
    map((action: Action) => action.payload),
    mergeMap((payload: any) => {
      // Making the flag as false at start
      isShowPageCallStarted = false;
      // Checking if SHOW_PAGE call is triggered in between by listening to SHOW_PAGE_STARTED event
      PubSubUtils.subscribe(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_STARTED, () => {
        isShowPageCallStarted = true;
      });
      const debugInfo =
        Debugger.getPreviewMode() && HeaderProcessor.addDebugHeaders({ debugId: Utils.generateUniqueID() });
      requestBody.dataContext = payload.payload.dataViewName;
      requestBody.dataContextParameters = payload.payload.dataViewKeys;
      // Need to remove the replacement logic after default parameters are passed from routed metadata.
      // Infinity merges are not stable and blocking bug so doing work around
      const { context, requestType, routeKey } = payload;
      let { routeEndpoint } = payload;
      if (routeEndpoint) {
        if (routeEndpoint.indexOf('viewType={viewType}') > 0) {
          routeEndpoint = routeEndpoint.replace('{viewType}', 'page');
        } else if (routeEndpoint.indexOf('viewType=""') > 0) {
          routeEndpoint = routeEndpoint.replace('viewType=""', 'viewType="page"');
        }
      }

      isGetObjectEndpoint = routeKey === actionType.ACTION_GETOBJECT;
      if (isGetObjectEndpoint) {
        ObjectTransformer.setNeedsMapping(true);
      }

      const url = services.getServerURL() + routeEndpoint;
      const fetchContext = payload.options.context;
      let data;
      const headers: ActionObject['headers'] = {};
      const className = payload?.payload?.caseClassName;
      if (RemoteCase.isRemoteCase(className)) {
        const { RemoteSystemID } = RemoteCase.getRemoteCase(className);
        headers.RemoteSystemID = RemoteSystemID;
      }

      if (EnvironmentInfo.isWSSPortal()) {
        headers[Constants.X_ORIGIN_CHANNEL] = 'WSS';
      }

      if (requestType === 'POST') {
        data = payload.payload;
        // viewType=page only for POST request if viewType is not present in the URL
        if (routeEndpoint.indexOf('viewType=') < 0) {
          routeEndpoint = `${routeEndpoint}?viewType=page`;
        }

        const actionCategory = getResourceType(routeKey);
        let body;
        switch (actionCategory) {
          case CASE:
          case OBJECT:
            body = { caseTypeID: data.caseTypeID, processID: data.processID, content: {} };
            break;
          case DATA:
            body = requestBody;
            break;
          default:
            body = data;
        }

        return from(
          fetch(
            url,
            {
              method: requestType,
              body,
              headers: {
                ...headers,
                ...debugInfo
              }
            },
            context
          )
        ).pipe(
          map((response) => {
            return {
              response: { ...response.data },
              payload,
              headers: processHeaders(response.headers, '', headers)
            };
          }),
          // logAndCatchError includes handling of problem to include calling the promise's reject
          logAndCatchError(': Error occurred during ajax call at historyEpic : ', payload)
        );
      }

      return from(
        fetch(
          url,
          {
            method: 'GET',
            headers: {
              ...headers,
              ...debugInfo
            }
          },
          fetchContext.substring(0, fetchContext.lastIndexOf('/'))
        )
      ).pipe(
        map((response) => {
          return {
            response: response.data,
            payload,
            headers: {
              ...processHeaders(response.headers),
              ...debugInfo
            }
          };
        }),
        catchError((error) => {
          if (routeKey === actionType.ACTION_LAUNCH_ASSIGNMENT) {
            const errorObject = {
              messages: [
                {
                  type: 'error',
                  message: error.response.data.errorDetails[0].localizedValue
                }
              ],
              category: 'PAGE'
            };
            NavigationUtils.setComponentCache('assignments-launchAssignment-Error', errorObject);

            const semanticAssignmentUrl = getSemanticURLWithRelatedQueryParams(
              BrowserHistory.getPathNameWithQueryParams()
            );
            const endPointInfo = RouteParser.getEndPointInfo(semanticAssignmentUrl.split('/assignments')[0]);

            const fetchUrl = services.getServerURL() + endPointInfo.routeEndpoint;
            payload = {
              ...payload,
              routeKey: actionType.ACTION_OPENWORKBYHANDLE,
              resetSemanticURL: true
            };

            return from(
              fetch(
                fetchUrl,
                {
                  method: endPointInfo.reqType,
                  headers: {
                    ...headers,
                    ...debugInfo
                  }
                },
                fetchContext.substring(0, fetchContext.lastIndexOf('/'))
              )
            ).pipe(
              map((response) => {
                return {
                  response: response.data,
                  payload,
                  headers: {
                    ...processHeaders(response.headers),
                    ...debugInfo
                  }
                };
              })
            );
          }
          return of(actions.error(': Error occurred during ajax call at historyEpic : ', error, context));
        })
      );
    }),
    mergeMap(async (data) => {
      // if already subscribed then no need to execute this block
      /* istanbul ignore next */
      if (isShowPageCallStarted) {
        return theReturn;
      }
      // Checking if SHOW_PAGE call is triggered in between by listening to SHOW_PAGE_STARTED event
      PubSubUtils.subscribe(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_STARTED, () => {
        isShowPageCallStarted = true;
      });
      if (data && data.type === actionType.ERROR) {
        return processDataToDispatchErrorAction(data);
      }
      // set STOPMERGEACTIONS equal to payload.caseViewMode which tells is view in review mode i.e ToDo
      const { headers = {}, response, payload } = data;
      const translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
        [publicConstants.STOPMERGEACTIONS]: !response.nextAssignmentInfo
      });
      const target = payload.options.acName;
      const context =
        isContainerSingleMode(target) && hasContainerItems(target)
          ? getActiveContainerItemName(target)
          : data.payload.options.context;
      const containerItemID = context;
      headers.RemoteSystemID = payload?.payload?.RemoteSystemID;
      const commonInfo = {
        target,
        context,
        containerItemID,
        headers,
        debugInfo: { debugId: headers[Constants.COREHEADER_APP_DEBUG_ID] }
      };

      let pageClass = payload?.payload?.pageClass;
      const pageName = payload?.payload?.page;
      const theType = isGetObjectEndpoint ? actionType.ACTION_GETOBJECT : actionType.ACTION_OPENWORKBYHANDLE;
      const id = isGetObjectEndpoint ? payload.payload.objectID : payload.payload.workID;
      const urlPlaceholder = isGetObjectEndpoint ? '{objectID}' : '{workID}';
      let semanticURL = isGetObjectEndpoint ? `objects/${id}` : '';

      if (payload?.payload?.caseName && id) {
        semanticURL = getSemanticURL(theType, {
          caseName: data.payload.payload.caseName
        });
        semanticURL = semanticURL?.replace(urlPlaceholder, id) || '';

        // Set page class to the case class since we are displaying a case page
        pageClass = payload?.payload?.caseClassName;
      }

      if (payload?.routeKey === actionType.ACTION_LAUNCH_ASSIGNMENT || payload?.resetSemanticURL) {
        resetSemanticURLFromAssignmentToCase(containerItemID, translatedDXAPIResponse);
      }

      setCoexistenceAction(payload?.routeKey, translatedDXAPIResponse);

      // Publish 'showPageInit' event before replacing existing container with new content.
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_INIT, {
        pageName,
        pageClass
      });

      const components = Utils.getIn(translatedDXAPIResponse, Constants.COMPONENTS);
      if (components) {
        await loadComponents(components);
      }

      publishResponse(translatedDXAPIResponse, payload.options.actionConfig, true);

      // Publish event for when a page is displayed
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.UI_EVENTS.SHOW_PAGE_DONE, {
        target,
        pageName,
        pageClass
      });

      let actionPayload = getActionPayload(translatedDXAPIResponse, commonInfo);
      const resourceType = getResourceType(data.payload.routeKey);
      actionPayload = getUpdatedPayloadBasedOnResourceType(
        resourceType,
        { ...response.data, semanticURL, caseViewMode: Constants.REVIEW_MODE },
        actionPayload
      );
      actionPayload.data.traditionalGadgetId = CaseUtils.getUniqueGadgetId();
      theReturn.push(getActionBasedOnContainerMode(target, actionPayload));
      handleLaunchAssignmentError(context, theReturn);
      addPreviewResetAction(theReturn);
      return theReturn;
    }),
    map(() => {
      if (!isShowPageCallStarted) {
        StateManager.dispatch(theReturn);
      }
      theReturn = [];
      isGetObjectEndpoint = false;
      isShowPageCallStarted = false;
      return theReturn;
    }),
    logAndCatchError('Error occurred')
  );

const historyEpics = [showHistory];

export default historyEpics;
